import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, Lock } from "lucide-react";
import { fetchApi } from "@/lib/apiBase";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Gallery", path: "/gallery" },
  { name: "About", path: "/about" },
  { name: "Policies", path: "/policies" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSubmark, setShowSubmark] = useState(true);
  const [announcementText, setAnnouncementText] = useState("2026 April Calendar Now Open!");
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    let isActive = true;
    const loadSiteSettings = async () => {
      try {
        const res = await fetchApi("/site-settings/public");
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !isActive) return;
        if (typeof data.announcement_banner_text === "string" && data.announcement_banner_text.trim()) {
          setAnnouncementText(data.announcement_banner_text.trim());
        }
      } catch (error) {
        // Keep fallback banner text when public settings are unavailable.
      }
    };
    loadSiteSettings();
    return () => {
      isActive = false;
    };
  }, []);

  return (
    <>
      {/* Announcement Banner */}
      <div className="announcement-banner py-2 text-center">
        <p className="text-xs tracking-wide">{announcementText}</p>
      </div>

      {/* Main Header */}
      <header
        data-testid="navbar"
        className={`sticky top-0 z-40 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between py-4">
            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#111111]"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo */}
            <Link
              to="/"
              data-testid="logo"
              className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 text-lg sm:text-xl md:text-2xl lg:static lg:translate-x-0"
            >
              {showSubmark && (
                <img
                  src="/images/submark-logo.png"
                  alt="DadaDeLuxe submark"
                  className="h-[1.45em] w-auto"
                  onError={() => setShowSubmark(false)}
                />
              )}
              <span className="logo-text text-[#111111]">
                DADADELUXE
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm tracking-wide transition-colors ${
                    location.pathname === link.path
                      ? "text-[#111111] font-medium"
                      : "text-[#666] hover:text-[#111111]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Book/Cart Button */}
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/admin"
                data-testid="nav-sign-in-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#d8c3b0] text-[#111111] text-sm hover:bg-[#f7f2ec] transition-colors"
              >
                <Lock size={14} />
                <span>Sign In</span>
              </Link>
              <Link
                to="/book"
                data-testid="book-now-nav-btn"
                className="cart-btn"
              >
                <ShoppingCart size={16} />
                <span>Book</span>
              </Link>
            </div>

            {/* Mobile Book Button */}
            <div className="sm:hidden flex items-center gap-1">
              <Link to="/admin" className="p-2" aria-label="Admin sign in">
                <Lock size={18} />
              </Link>
              <Link
                to="/book"
                className="p-2"
                aria-label="Book appointment"
              >
                <ShoppingCart size={20} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-[70] lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? "visible" : "invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 mobile-menu-overlay transition-opacity duration-300 ${
            isMobileMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          data-testid="mobile-menu"
          className={`absolute top-0 left-0 h-full w-72 bg-white shadow-2xl transition-transform duration-300 ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="p-6 pt-8">
            <div className="mb-8 grid grid-cols-[40px_1fr_40px] items-center">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full text-[#111111] hover:bg-[#f4f4f4]"
                aria-label="Close mobile menu"
              >
                <X size={28} />
              </button>
              <div className="flex items-center justify-center">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 text-center"
                >
                  {showSubmark && (
                    <img
                      src="/images/submark-logo.png"
                      alt="DadaDeLuxe submark"
                      className="h-7 w-auto"
                      onError={() => setShowSubmark(false)}
                    />
                  )}
                  <span className="logo-text text-sm text-[#111111]">
                    DADADELUXE
                  </span>
                </Link>
              </div>
              <span aria-hidden="true" className="h-10 w-10" />
            </div>
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`text-base py-2 border-b border-gray-100 ${
                    location.pathname === link.path
                      ? "text-[#111111] font-medium"
                      : "text-[#666]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <Link
              to="/book"
              data-testid="mobile-book-btn"
              className="btn-black w-full text-center mt-8 block"
            >
              Book Appointment
            </Link>
            <Link
              to="/admin"
              className="w-full text-center mt-3 block px-4 py-3 rounded-full border border-[#d8c3b0] text-[#111111] text-sm hover:bg-[#f7f2ec] transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
